// src/admin/AnnouncementsAdmin.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';

interface Announcement {
  id: number;
  title: string;
  summary: string;
  content: string;
  image: string | null;
  status: 'draft' | 'published';
  is_featured: number;
  views: number;
  created_at: string;
  updated_at: string | null;
  author_name?: string;
}

const AnnouncementsAdmin: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  // ── بارگذاری لیست اطلاعیه‌ها ──────────────────────────
  const loadItems = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch('https://fellfell.ir/api/announcements.php', {
        credentials: 'include'
      });
      const data = await res.json();
      if (data.success) {
        setItems(data.data || []);
      } else {
        setError('خطا در دریافت اطلاعیه‌ها');
      }
    } catch (err) {
      setError('خطا در ارتباط با سرور');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, []);

  // ── تغییر وضعیت (انتشار / پیش‌نویس) ──────────────────
  const handleToggleStatus = async (id: number, currentStatus: string) => {
    const newStatus = currentStatus === 'published' ? 'draft' : 'published';
    try {
      const res = await fetch(`https://fellfell.ir/api/announcements.php?id=${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus })
      });
      const data = await res.json();
      if (data.success) {
        showToast(`وضعیت به ${newStatus === 'published' ? 'منتشر شده' : 'پیش‌نویس'} تغییر یافت`, 'success');
        loadItems();
      } else {
        showToast('خطا در تغییر وضعیت', 'error');
      }
    } catch {
      showToast('خطا در تغییر وضعیت', 'error');
    }
  };

  // ── تغییر وضعیت ویژه ──────────────────────────────────
  const handleToggleFeatured = async (id: number, currentFeatured: number) => {
    const newFeatured = currentFeatured === 1 ? 0 : 1;
    try {
      const res = await fetch(`https://fellfell.ir/api/announcements.php?id=${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ is_featured: newFeatured })
      });
      const data = await res.json();
      if (data.success) {
        showToast(newFeatured === 1 ? '⭐ ویژه شد' : 'ویژه لغو شد', 'success');
        loadItems();
      } else {
        showToast('خطا در تغییر وضعیت ویژه', 'error');
      }
    } catch {
      showToast('خطا در تغییر وضعیت ویژه', 'error');
    }
  };

  // ── حذف اطلاعیه ──────────────────────────────────────
  const handleDelete = async (id: number) => {
    if (!confirm('آیا از حذف این اطلاعیه مطمئن هستید؟')) return;
    try {
      const res = await fetch(`https://fellfell.ir/api/announcements.php?id=${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      const data = await res.json();
      if (data.success) {
        showToast('اطلاعیه حذف شد', 'success');
        loadItems();
      } else {
        showToast('خطا در حذف', 'error');
      }
    } catch {
      showToast('خطا در حذف', 'error');
    }
  };

  if (loading) {
    return (
      <div style={{ direction: 'rtl', textAlign: 'center', padding: '50px' }}>
        ⏳ در حال بارگذاری اطلاعیه‌ها...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ direction: 'rtl', padding: '20px' }}>
        <div style={{ background: '#ffebee', color: '#c62828', padding: '20px', borderRadius: '12px' }}>
          ❌ {error}
          <button onClick={loadItems} style={{ marginRight: '15px', padding: '5px 15px', background: '#c62828', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
            تلاش مجدد
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ direction: 'rtl', padding: '20px' }}>
      {/* ── Toast ────────────────────────────────────────── */}
      {toast && (
        <div style={{
          position: 'fixed',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          padding: '14px 28px',
          borderRadius: '12px',
          fontSize: '15px',
          zIndex: 9999,
          background: toast.type === 'success' ? '#2e7d32' : '#c62828',
          color: '#fff',
          boxShadow: '0 6px 25px rgba(0,0,0,0.25)',
          animation: 'slideDown 0.3s ease-out'
        }}>
          {toast.message}
        </div>
      )}

      {/* ── هدر ──────────────────────────────────────────── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1 style={{ color: '#1e3a1e' }}>📢 مدیریت اطلاعیه‌ها</h1>
        <Link
          to="/admin/announcements/add"
          style={{
            padding: '10px 20px',
            background: '#2e7d32',
            color: '#fff',
            borderRadius: '8px',
            textDecoration: 'none',
            fontWeight: '600'
          }}
        >
          ➕ افزودن اطلاعیه جدید
        </Link>
      </div>

      {/* ── لیست اطلاعیه‌ها ────────────────────────────── */}
      {items.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', background: '#fff', borderRadius: '12px' }}>
          <p style={{ fontSize: '18px', color: '#888' }}>📭 هیچ اطلاعیه‌ای ثبت نشده است</p>
          <p style={{ color: '#aaa' }}>برای افزودن، روی دکمه "افزودن اطلاعیه جدید" کلیک کنید.</p>
        </div>
      ) : (
        <div style={{ background: '#fff', borderRadius: '12px', overflow: 'auto', boxShadow: '0 2px 10px rgba(0,0,0,0.06)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '900px' }}>
            <thead>
              <tr style={{ background: '#f5faf5' }}>
                <th style={{ padding: '12px 15px', textAlign: 'right', borderBottom: '2px solid #e8f0e8' }}>عنوان</th>
                <th style={{ padding: '12px 15px', textAlign: 'right', borderBottom: '2px solid #e8f0e8' }}>وضعیت</th>
                <th style={{ padding: '12px 15px', textAlign: 'right', borderBottom: '2px solid #e8f0e8' }}>ویژه</th>
                <th style={{ padding: '12px 15px', textAlign: 'right', borderBottom: '2px solid #e8f0e8' }}>بازدید</th>
                <th style={{ padding: '12px 15px', textAlign: 'right', borderBottom: '2px solid #e8f0e8' }}>تاریخ</th>
                <th style={{ padding: '12px 15px', textAlign: 'center', borderBottom: '2px solid #e8f0e8' }}>عملیات</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} style={{ borderBottom: '1px solid #eef5ee' }}>
                  <td style={{ padding: '12px 15px', fontWeight: '500' }}>
                    {item.title}
                    {item.status === 'draft' && (
                      <span style={{ marginRight: '8px', fontSize: '11px', color: '#f57c00', background: '#fff3e0', padding: '2px 8px', borderRadius: '12px' }}>
                        پیش‌نویس
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '12px 15px' }}>
                    <button
                      onClick={() => handleToggleStatus(item.id, item.status)}
                      style={{
                        padding: '4px 14px',
                        borderRadius: '20px',
                        fontSize: '12px',
                        border: 'none',
                        cursor: 'pointer',
                        background: item.status === 'published' ? '#e8f5e9' : '#ffebee',
                        color: item.status === 'published' ? '#2e7d32' : '#c62828',
                        fontWeight: '600'
                      }}
                    >
                      {item.status === 'published' ? '✅ منتشر شده' : '📝 پیش‌نویس'}
                    </button>
                  </td>
                  <td style={{ padding: '12px 15px' }}>
                    <button
                      onClick={() => handleToggleFeatured(item.id, item.is_featured)}
                      style={{
                        padding: '4px 14px',
                        borderRadius: '20px',
                        fontSize: '12px',
                        border: 'none',
                        cursor: 'pointer',
                        background: item.is_featured === 1 ? '#fff3e0' : '#f5f5f5',
                        color: item.is_featured === 1 ? '#e65100' : '#888'
                      }}
                    >
                      {item.is_featured === 1 ? '⭐ ویژه' : 'عادی'}
                    </button>
                  </td>
                  <td style={{ padding: '12px 15px' }}>{item.views}</td>
                  <td style={{ padding: '12px 15px', fontSize: '13px', color: '#888' }}>
                    {new Date(item.created_at).toLocaleDateString('fa-IR')}
                  </td>
                  <td style={{ padding: '12px 15px', textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                      <button
                        onClick={() => navigate(`/admin/announcements/edit/${item.id}`)}
                        style={{
                          padding: '6px 12px',
                          background: '#e3f2fd',
                          color: '#0d47a1',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer'
                        }}
                        title="ویرایش"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => handleDelete(item.id)}
                        style={{
                          padding: '6px 12px',
                          background: '#ffebee',
                          color: '#c62828',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer'
                        }}
                        title="حذف"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <style>{`
        @keyframes slideDown {
          from { opacity: 0; transform: translateX(-50%) translateY(-20px); }
          to { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default AnnouncementsAdmin;